/**
 * MediaLightbox — full-screen viewer for images / video.
 *
 * Owner-spec 2026-05-31 (Phase 1): tap an image → opens this modal with
 * prev/next nav, download button, and a thumbnail strip across the
 * bottom. WhatsApp Web-ish; not pixel-perfect, but the same affordances.
 *
 * Receives the full media list pre-built by `buildMediaList` (from
 * MessageBubble.js) plus the current index. Parent owns the index so
 * keyboard nav, message-stream updates, and library-drawer jumps all
 * stay in one place.
 *
 * Keyboard:
 *   ArrowLeft / ArrowRight — prev / next
 *   Esc — close
 */

import React, { useEffect, useCallback } from 'react';

const overlay = {
  position: 'fixed',
  top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.92)',
  zIndex: 10000,
  display: 'flex',
  flexDirection: 'column',
};

const toolbarBtn = (disabled = false) => ({
  background: 'rgba(255,255,255,0.08)',
  color: disabled ? '#666' : 'white',
  border: 'none',
  borderRadius: '50%',
  width: '36px',
  height: '36px',
  cursor: disabled ? 'default' : 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '16px',
});

const formatTs = (ts) => {
  if (!ts) return '';
  const d = new Date(typeof ts === 'number' && ts < 1e12 ? ts * 1000 : ts);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('he-IL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const MediaLightbox = ({
  mediaList,
  currentIndex,
  onClose,
  onPrev,
  onNext,
  onJumpTo,
}) => {
  const current = mediaList && mediaList[currentIndex];

  // Keyboard nav.
  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') onClose && onClose();
    else if (e.key === 'ArrowLeft') onNext && onNext(); // RTL: left = next
    else if (e.key === 'ArrowRight') onPrev && onPrev();
  }, [onClose, onPrev, onNext]);

  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  if (!current) return null;

  const isFirst = currentIndex <= 0;
  const isLast = currentIndex >= mediaList.length - 1;

  return (
    <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 18px',
        gap: '8px',
        background: 'rgba(0,0,0,0.4)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <a
            href={current.url}
            download={current.filename || ''}
            style={{ ...toolbarBtn(), textDecoration: 'none' }}
            title="הורד"
          >
            ⬇
          </a>
          <a
            href={current.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...toolbarBtn(), textDecoration: 'none' }}
            title="פתח בכרטיסייה חדשה"
          >
            ↗
          </a>
        </div>

        <div style={{
          color: 'white',
          fontSize: '13px',
          textAlign: 'center',
          flex: 1,
        }}>
          {current.caption || current.filename || ''}
          <div style={{ fontSize: '11px', color: '#bbb', marginTop: '2px' }}>
            {formatTs(current.ts)} · {currentIndex + 1} / {mediaList.length}
          </div>
        </div>

        <button onClick={onClose} style={toolbarBtn()} title="סגור">✕</button>
      </div>

      {/* Main viewer */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Prev arrow */}
        <button
          onClick={onPrev}
          disabled={isFirst}
          style={{
            ...toolbarBtn(isFirst),
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '44px',
            height: '44px',
            fontSize: '22px',
          }}
          title="קודם"
        >
          ›
        </button>

        {current.type === 'video' ? (
          <video
            src={current.url}
            controls
            autoPlay
            style={{
              maxWidth: '92vw',
              maxHeight: '76vh',
              objectFit: 'contain',
              borderRadius: '4px',
            }}
          />
        ) : (
          <img
            src={current.url}
            alt={current.caption || 'תמונה'}
            style={{
              maxWidth: '92vw',
              maxHeight: '76vh',
              objectFit: 'contain',
              borderRadius: '4px',
              userSelect: 'none',
            }}
          />
        )}

        {/* Next arrow */}
        <button
          onClick={onNext}
          disabled={isLast}
          style={{
            ...toolbarBtn(isLast),
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '44px',
            height: '44px',
            fontSize: '22px',
          }}
          title="הבא"
        >
          ‹
        </button>
      </div>

      {/* Thumbnail strip */}
      {mediaList.length > 1 && (
        <div style={{
          display: 'flex',
          gap: '6px',
          padding: '10px 14px',
          background: 'rgba(0,0,0,0.4)',
          overflowX: 'auto',
        }}>
          {mediaList.map((item, idx) => (
            <button
              key={item.id || idx}
              onClick={() => onJumpTo && onJumpTo(idx)}
              style={{
                width: '60px',
                height: '60px',
                padding: 0,
                background: '#222',
                border: idx === currentIndex
                  ? '2px solid #25D366'
                  : '2px solid transparent',
                borderRadius: '4px',
                cursor: 'pointer',
                flexShrink: 0,
                overflow: 'hidden',
              }}
              title={item.caption || ''}
            >
              {item.type === 'video' ? (
                <div style={{
                  width: '100%', height: '100%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontSize: '20px', background: '#333',
                }}>
                  ▶
                </div>
              ) : (
                <img
                  src={item.url}
                  alt=""
                  loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaLightbox;
